import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, KeyRound, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";
import ThemeToggle from "../components/ThemeToggle";
import BrandLogo from "../components/BrandLogo";

export default function ResetPassword() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast({ title: "Missing token", description: "Open the reset link from your email.", variant: "destructive" });
      return;
    }
    if (newPw.length < 8) {
      toast({ title: "Password too short", description: "Use at least 8 characters.", variant: "destructive" });
      return;
    }
    if (newPw !== confirmPw) {
      toast({ title: "Passwords don't match", description: "Retype the new password.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPw });
      toast({ title: "Password updated", description: "You can sign in with your new password now." });
      navigate("/login");
    } catch (err) {
      toast({ title: "Reset failed", description: err?.response?.data?.detail || "Please request a new reset link.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen login-bg flex flex-col">
      <div className="absolute top-4 right-4 z-10"><ThemeToggle /></div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center mb-8">
            <BrandLogo variant="full" className="h-48 w-auto" />
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-xl shadow-primary/5 p-6 sm:p-8" data-testid="reset-password-card">
            <div className="flex items-center gap-2 text-primary text-[12px] font-semibold uppercase tracking-wider">
              <ShieldCheck className="w-4 h-4" /> Choose a new password
            </div>
            <h1 className="mt-2 text-[22px] font-semibold tracking-tight">Set your password</h1>
            <p className="text-[13px] text-muted-foreground mt-1">Pick something you haven&apos;t used here before (min 8 characters).</p>

            {!token && (
              <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 text-destructive text-[12.5px] p-2.5">
                No reset token found in the URL. Please use the link from your reset email.
              </div>
            )}

            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-password">New password</Label>
                <Input id="new-password" data-testid="reset-new-password-input" type="password" autoComplete="new-password"
                  value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={8} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input id="confirm-password" data-testid="reset-confirm-password-input" type="password" autoComplete="new-password"
                  value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required minLength={8} />
              </div>
              <Button type="submit" data-testid="reset-submit-button" disabled={busy || !token} className="w-full h-10 gap-2">
                <KeyRound className="w-4 h-4" /> {busy ? "Updating…" : "Update password"}
              </Button>
            </form>

            <div className="mt-6 border-t border-border pt-4">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-[12.5px] text-primary hover:underline">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </Link>
            </div>
          </div>
          <div className="mt-6 text-center text-[11.5px] text-muted-foreground">
            © {new Date().getFullYear()} UC Vigil · Internal staff portal
          </div>
        </div>
      </div>
    </div>
  );
}
